import { carregarGtm } from '@shared/cookie-consent/utils/carregarGtm';
import {
  COOKIE_CONSENTIMENTO_NOME,
  calcularExpiracaoConsentimento,
  isConsentimentoConcedido,
  resolverConsentimento,
  resolverDominioCookie,
} from '@shared/cookie-consent/utils/cookieConsentRules';
import { publicarPageviewVirtual } from '@shared/marketing-tracking/utils/dataLayerMarketing';
import { ref } from 'vue';
import { useRouter } from 'vue-router';
import { cookieHistoricoService, cookieService, storageService } from '@/services';

function apagarTodosOsCookies(dominio: string | null) {
  Object.keys(cookieService.getCookies())
    .filter(nome => nome !== COOKIE_CONSENTIMENTO_NOME)
    .forEach(nome => cookieService.deleteCookie(nome, dominio));
}

function salvarEscolha(aceito: boolean, dominio: string | null) {
  storageService.meuacerto.set({ popup: false, ativo: aceito });
  cookieService.createCookie(COOKIE_CONSENTIMENTO_NOME, String(aceito), {
    path: '/',
    domain: dominio ?? undefined,
    expires: calcularExpiracaoConsentimento(new Date()).toUTCString(),
  });
}

/**
 * Consentimento de cookies (LGPD). Ao rejeitar, apaga todos os cookies do domínio
 * (não só os de terceiros/analytics) — comportamento intencional, não um bug.
 */
export function useCookieConsent() {
  const router = useRouter();
  const dominio = resolverDominioCookie(window.location.hostname);
  const armazenado = storageService.meuacerto.get() ?? {};
  const cookieAtual = cookieService.getCookie(COOKIE_CONSENTIMENTO_NOME);
  const consentimento = resolverConsentimento(cookieAtual, armazenado);

  if (consentimento.popup !== armazenado.popup || consentimento.ativo !== armazenado.ativo) {
    storageService.meuacerto.set(consentimento);
  }

  const exibirBanner = ref(consentimento.popup === true);

  if (!exibirBanner.value && consentimento.ativo === false) {
    apagarTodosOsCookies(dominio);
  }

  if (isConsentimentoConcedido(consentimento)) {
    carregarGtm();
  }

  /**
   * Fecha o banner antes do POST: com ele aberto durante a requisição, cliques repetidos
   * duplicavam o histórico e um "Rejeitar" no meio era sobrescrito pelo aceite atrasado.
   * A tela em que o consumidor aceitou já foi navegada sem consentimento, então o pageview dela
   * sai aqui; sem isso, a página de entrada do anúncio não chegaria às plataformas.
   */
  async function aceitar() {
    salvarEscolha(true, dominio);
    exibirBanner.value = false;
    carregarGtm();
    publicarPageviewVirtual(router.currentRoute.value);

    try {
      await cookieHistoricoService.registrarAceite();
    } catch (e) {
      logger.warn('Erro ao registrar histórico de aceite de cookies:', e);
      logger.tracker.error('Erro ao registrar histórico de aceite de cookies', { originalError: e });
    }
  }

  function rejeitar() {
    apagarTodosOsCookies(dominio);
    salvarEscolha(false, dominio);
    exibirBanner.value = false;
  }

  return {
    exibirBanner,
    aceitar,
    rejeitar,
  };
}
